// **Schlüssel und gemeinsame Abfragen.** Schlüssel als Arrays, Bereich zuerst:
// Alles eines Mandanten hängt unter `["tenant", id, …]`, damit ein beendeter
// Lauf mit einem `invalidateQueries({ queryKey: ["tenant", id] })` die ganze
// Detailseite frisch holt — das, was früher „die Ansicht neu bauen" war.
//
// Abfragen, die nur eine Ansicht braucht, stehen bei ihr; hier nur, was
// mehrere teilen.

import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { get } from "./client";
import type { Catalog, HostCapacity, LicenseTemplateUsage, ReleasesResponse, TenantListItem, TenantResponse } from "./types";

export const keys = {
  me: ["me"] as const,
  catalog: ["catalog"] as const,
  dashboard: ["dashboard"] as const,
  host: (wait: boolean) => ["host", wait] as const,
  tenants: ["tenants"] as const,
  tenant: (id: number) => ["tenant", id] as const,
  tenantDetail: (id: number) => ["tenant", id, "detail"] as const,
  tenantUsers: (id: number) => ["tenant", id, "users"] as const,
  tenantAudit: (id: number) => ["tenant", id, "audit"] as const,
  tenantUsage: (id: number, month: string) => ["tenant", id, "usage", month] as const,
  tenantInstance: (id: number) => ["tenant", id, "instance"] as const,
  tenantEnv: (id: number) => ["tenant", id, "env"] as const,
  instanceModels: (id: number) => ["tenant", id, "instance-models"] as const,
  instanceTemplates: (id: number) => ["tenant", id, "instance-templates"] as const,
  gatewayModels: (id: number, which: string) => ["tenant", id, "gateway-models", which] as const,
  licenseTemplates: ["license-templates"] as const,
  feedbackOverview: ["feedback", "overview"] as const,
  feedbackList: (filter: Record<string, string>) => ["feedback", "list", filter] as const,
  feedbackReport: (id: number) => ["feedback", "report", id] as const,
  costs: (month: string) => ["costs", month] as const,
  audit: (tenant: string) => ["audit", tenant] as const,
  releases: ["releases"] as const,
  platformUpdate: ["platform", "update"] as const,
};

export const fetchCatalog = () => get<Catalog>("/catalog");

/**
 * **Der Katalog** (Features, Integrationen, Lizenz-Vorlagen …). `main.tsx`
 * lädt ihn vor der ersten Ansicht, deshalb wartet hier nie jemand; wer eine
 * Lizenz-Vorlage ändert, invalidiert ihn, statt ihn zu verändern.
 */
export function useCatalog(): Catalog {
  return useSuspenseQuery({ queryKey: keys.catalog, queryFn: fetchCatalog, staleTime: Infinity }).data;
}

/** Ein Mandant mit letztem Lauf und Prüfergebnissen — Wizard und Detailseite. */
export function useTenant(id: number) {
  return useQuery({ queryKey: keys.tenantDetail(id), queryFn: () => get<TenantResponse>(`/tenants/${id}`) });
}

export function useTenants() {
  return useQuery({ queryKey: keys.tenants, queryFn: async () => (await get<{ tenants: TenantListItem[] }>("/tenants")).tenants });
}

/** Der Host; `wait` wartet auf frische Werte (`docker stats`, Sekunden). */
export function useHost(wait = false, enabled = true) {
  return useQuery({
    queryKey: keys.host(wait),
    queryFn: async () => (await get<{ host: HostCapacity | null }>(`/host${wait ? "?wait=1" : ""}`)).host,
    enabled,
  });
}

export function useLicenseTemplates() {
  return useQuery({
    queryKey: keys.licenseTemplates,
    queryFn: async () => (await get<{ templates: LicenseTemplateUsage[] }>("/license-templates")).templates,
  });
}

/** Die ausrollbaren Builds — eine Minute frisch (der Server fragt die Registry ebenso selten). */
export function useReleases() {
  return useQuery({ queryKey: keys.releases, queryFn: () => get<ReleasesResponse>("/releases"), staleTime: 60_000 });
}
